import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { getProgressByStudyId } from "../../services/ProgressService";
import DataTable from "react-data-table-component";
import HSTSCustomStyle from "../../utils/hstsCustomStyle";
import { SideBar } from "./inc/sideBar";
import { TopHeader } from "./inc/topHeader";
import Chart from "react-apexcharts";
import CountUp from "react-countup";
import OverallProgressPDF from "./overallProgressPDF";
import { getStudyById } from "../../services/StudyService";
import { getStudentById } from "../../services/StudentService";

const ManageProgressC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeMenu, setActiveMenu] = useState(null);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1024
  );

  const { id } = useParams();
  const [progressList, setProgressList] = useState([]);
  const [student, setStudent] = useState(null);
  const [study, setStudy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigator = useNavigate();

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (windowWidth < 768) {
      setSidebarOpen(false);
    }
  }, [windowWidth]);

  const toggleMenu = (menu) => {
    setActiveMenu(activeMenu === menu ? null : menu);
  };

  useEffect(() => {
    const fetchProgress = async () => {
      try {
        const studyResponse = await getStudyById(id);
        setStudy(studyResponse.data);

        const studentId =
          studyResponse.data.student_id || studyResponse.data.studentId;
        const studentResponse = await getStudentById(studentId);
        setStudent(studentResponse.data);

        const response = await getProgressByStudyId(id);
        const progressData = Array.isArray(response.data)
          ? response.data
          : response.data?.data || [];
        setProgressList(progressData);
      } catch (err) {
        console.error("Error fetching progress:", err);
        setError("Failed to load progress data.");
      } finally {
        setLoading(false);
      }
    };

    fetchProgress();
  }, [id]);

  const getProgressStatus = (progress) => {
    const meetProgressPlan =
      progress.meet_progress_plan || progress.meetProgressPlan;
    const isProgressPlanGood =
      meetProgressPlan && meetProgressPlan.toUpperCase() === "YES";

    const studyId = progress.studyId;
    const finishDate = studyId
      ? new Date(studyId.finish_date || studyId.finishDate)
      : null;

    const submitThesis = progress.submit_thesis || progress.submitThesis;
    const isSubmitThesisOnTime =
      finishDate && submitThesis ? new Date(submitThesis) <= finishDate : false;

    const studyComplition =
      progress.study_complition || progress.studyComplition;
    const isStudyCompletionOnTime =
      finishDate && studyComplition
        ? new Date(studyComplition) <= finishDate
        : false;

    const metCriteriaCount =
      (isProgressPlanGood ? 1 : 0) +
      (isSubmitThesisOnTime ? 1 : 0) +
      (isStudyCompletionOnTime ? 1 : 0);

    if (metCriteriaCount === 3) {
      return "Good";
    } else if (metCriteriaCount === 2) {
      return "Moderate";
    } else {
      return "Poor";
    }
  };

  const viewMore = (progressId) =>
    navigator(`/coordinator/viewProgress/details/${progressId}`);

  const columns = [
    {
      name: "S/N",
      selector: (row, index) => index + 1,
      width: "70px",
    },
    {
      name: "Previous progress",
      selector: (row) => (row.previous || "")?.slice(0, 30) + "...",
      wrap: true,
    },
    {
      name: "Currently progress",
      selector: (row) => (row.currently || "")?.slice(0, 30) + "...",
      wrap: true,
    },
    {
      name: "Next progress",
      selector: (row) => (row.next || "")?.slice(0, 30) + "...",
      wrap: true,
    },
    {
      name: "Supervisor Meeting",
      selector: (row) => row.meet_supervisor || row.meetSupervisor || "N/A",
    },
    {
      name: "Thesis Submitted",
      selector: (row) => row.submit_thesis || row.submitThesis || "N/A",
    },
    {
      name: "Submitted date",
      selector: (row) => row.progress_date || row.progressDate || "N/A",
    },
    {
      name: "More",
      cell: (row) => (
        <button
          className="btn btn-link text-center fs-4 text-primary zis-ponter"
          title="View details"
          onClick={() => viewMore(row.progress_id || row.progressId)}
        >
          <i className="fas fa-eye"></i>
        </button>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "80px",
    },
  ];

  return (
    <div className="d-flex position-relative" style={{ minHeight: "100vh" }}>
      <SideBar
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        activeMenu={activeMenu}
        toggleMenu={toggleMenu}
        windowWidth={windowWidth}
      />
      <div className="main-panel">
        <TopHeader
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          windowWidth={windowWidth}
        />
        <div className="container mt-0">
          <div className="page-inner">
            <div className="page-header bg-white zis-shadow rounded-2 mt-2">
              <ul className="breadcrumbs my-md-3 mb-3 ms-0 px-4">
                <li className="nav-home">
                  <Link to="/coordinator">
                    <span className="fa fa-home"></span>
                  </Link>
                </li>

                <li className="separator">
                  <i class="fas fa-chevron-right text-secondary"></i>
                </li>
                <li className="nav-item text-black fw-semibold">
                  <Link to="/coordinator/student">Manage student</Link>
                </li>
                <li className="separator">
                  <i class="fas fa-chevron-right text-secondary"></i>
                </li>
                <li className="nav-item text-black fw-semibold">
                  <button
                    className="btn btn-link p-0 text-decoration-none text-black"
                    onClick={() => navigator(-1)}
                  >
                    Student informations
                  </button>
                </li>
                <li className="separator">
                  <i class="fas fa-chevron-right text-secondary"></i>
                </li>
                <li className="nav-item text-black fw-semibold">
                  <Link to="#">Manage progress</Link>
                </li>
              </ul>
            </div>

            {progressList.length > 0 &&
              (() => {
                let goodCount = 0;
                let moderateCount = 0;
                let poorCount = 0;

                progressList.forEach((progress) => {
                  const status = getProgressStatus(progress);
                  if (status === "Good") goodCount++;
                  else if (status === "Moderate") moderateCount++;
                  else poorCount++;
                });

                const total = goodCount + moderateCount + poorCount;

                const goodPercentage = total
                  ? ((goodCount / total) * 100).toFixed(1)
                  : 0;
                const moderatePercentage = total
                  ? ((moderateCount / total) * 100).toFixed(1)
                  : 0;
                const poorPercentage = total
                  ? ((poorCount / total) * 100).toFixed(1)
                  : 0;

                return (
                  <div className="row g-3 mb-4">
                    <div className="col-12 col-sm-6 col-md-6 col-xl-3">
                      <div className="card zis-shadow rounded-2 border-0">
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <div>
                              <h5 className="fw-bold mb-0 text-success">
                                Good Progress
                              </h5>
                              <p className="text-muted mb-1">
                                All 3 criteria met
                              </p>
                            </div>
                            <h3 className="fw-bold text-success fs-1">
                              <CountUp start={0} end={goodCount} duration={2} />
                            </h3>
                          </div>

                          <div className="progress progress-sm">
                            <div
                              className="progress-bar bg-success"
                              role="progressbar"
                              style={{ width: `${goodPercentage}%` }}
                              aria-valuenow={goodPercentage}
                              aria-valuemin={0}
                              aria-valuemax={100}
                            ></div>
                          </div>

                          <div className="d-flex justify-content-between mt-2">
                            <p className="text-muted mb-0">Percentage</p>
                            <p className="text-muted mb-0">{goodPercentage}%</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-12 col-sm-6 col-md-6 col-xl-3">
                      <div className="card zis-shadow rounded-2 border-0">
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <div>
                              <h5 className="fw-bold mb-0 text-warning">
                                Moderate Progress
                              </h5>
                              <p className="text-muted mb-1">2 criteria met</p>
                            </div>
                            <h3 className="fw-bold text-warning fs-1">
                              <CountUp
                                start={0}
                                end={moderateCount}
                                duration={2}
                              />
                            </h3>
                          </div>

                          <div className="progress progress-sm">
                            <div
                              className="progress-bar bg-warning"
                              role="progressbar"
                              style={{ width: `${moderatePercentage}%` }}
                              aria-valuenow={moderatePercentage}
                              aria-valuemin={0}
                              aria-valuemax={100}
                            ></div>
                          </div>

                          <div className="d-flex justify-content-between mt-2">
                            <p className="text-muted mb-0">Percentage</p>
                            <p className="text-muted mb-0">
                              {moderatePercentage}%
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-12 col-sm-6 col-md-6 col-xl-3">
                      <div className="card zis-shadow rounded-2 border-0">
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <div>
                              <h5 className="fw-bold mb-0 text-danger">
                                Poor Progress
                              </h5>
                              <p className="text-muted mb-1">
                                0 or 1 criterion met
                              </p>
                            </div>
                            <h3 className="fw-bold text-danger fs-1">
                              <CountUp start={0} end={poorCount} duration={2} />
                            </h3>
                          </div>

                          <div className="progress progress-sm">
                            <div
                              className="progress-bar bg-danger"
                              role="progressbar"
                              style={{ width: `${poorPercentage}%` }}
                              aria-valuenow={poorPercentage}
                              aria-valuemin={0}
                              aria-valuemax={100}
                            ></div>
                          </div>

                          <div className="d-flex justify-content-between mt-2">
                            <p className="text-muted mb-0">Percentage</p>
                            <p className="text-muted mb-0">{poorPercentage}%</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-12 col-sm-6 col-md-6 col-xl-3">
                      <div className="card zis-shadow rounded-2 border-0">
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <div>
                              <h5 className="fw-bold mb-0 text-secondary">
                                Total Evaluations
                              </h5>
                              <p className="text-muted mb-1">
                                All recorded progress
                              </p>
                            </div>
                            <h3 className="fw-bold text-secondary fs-1">
                              <CountUp start={0} end={total} duration={2} />
                            </h3>
                          </div>

                          <div className="progress progress-sm">
                            <div
                              className="progress-bar bg-secondary"
                              role="progressbar"
                              style={{ width: "100%" }}
                              aria-valuenow={100}
                              aria-valuemin={0}
                              aria-valuemax={100}
                            ></div>
                          </div>

                          <div className="d-flex justify-content-between mt-2">
                            <p className="text-muted mb-0">
                              Overall Study Progress
                            </p>
                            <p className="text-muted mb-0">100%</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}

            <div className="card zis-shadow rounded-2">
              <div className="card-header">
                <div className="d-flex">
                  <h5 className="fw-bold mb-0">Monthly Study Evaluation</h5>
                  <div className="ms-auto d-none d-sm-inline">
                    {student && study && (
                      <OverallProgressPDF
                        progressList={progressList}
                        getProgressStatus={getProgressStatus}
                        student={{
                          name: student.data.full_name || student.data.fullName,
                          university:
                            study.univeristy_name || study.univeristyName,
                          gender: student.data.gender,
                          program: study.level,
                          startDate: study.start_date || study.startDate,
                          endDate: study.finish_date || study.finishDate,
                        }}
                      />
                    )}
                  </div>
                  <div></div>
                </div>
              </div>
              <div className="card-header d-sm-none">
                <div className="d-flex">
                  <div className="ms-auto">
                    {student && study && (
                      <OverallProgressPDF
                        progressList={progressList}
                        getProgressStatus={getProgressStatus}
                        student={{
                          name: student.data.full_name || student.data.fullName,
                          university:
                            study.univeristy_name || study.univeristyName,
                          gender: student.data.gender,
                          program: study.level,
                          startDate: study.start_date || study.startDate,
                          endDate: study.finish_date || study.finishDate,
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>
              <div className="card-body">
                {progressList.length === 0 ? (
                  <p className="text-muted text-center mb-0">
                    No progress data available for monthly evaluation.
                  </p>
                ) : (
                  (() => {
                    //   const monthMap = {};

                    //   const calculateMetCount = (progress) => {
                    //     const meetProgressPlan =
                    //       progress.meet_progress_plan ||
                    //       progress.meetProgressPlan;
                    //     const isMeetProgressPlanYes =
                    //       meetProgressPlan &&
                    //       meetProgressPlan.toUpperCase() === "YES";

                    //     const submitThesis =
                    //       progress.submit_thesis || progress.submitThesis;
                    //     const studyId = progress.studyId;
                    //     const finishDate = studyId
                    //       ? new Date(studyId.finish_date || studyId.finishDate)
                    //       : null;

                    //     const isSubmitThesisOnTime =
                    //       submitThesis && finishDate
                    //         ? new Date(submitThesis) <= finishDate
                    //         : false;

                    //     const studyComplition =
                    //       progress.study_complition || progress.studyComplition;
                    //     const isStudyCompletionOnTime =
                    //       studyComplition && finishDate
                    //         ? new Date(studyComplition) <= finishDate
                    //         : false;

                    //     const metCount = [
                    //       isMeetProgressPlanYes,
                    //       isSubmitThesisOnTime,
                    //       isStudyCompletionOnTime,
                    //     ].filter(Boolean).length;

                    //     return metCount;
                    //   };

                    //   progressList.forEach((progress) => {
                    //     const progressDate =
                    //       progress.progress_date || progress.progressDate;
                    //     if (!progressDate) return;

                    //     const date = new Date(progressDate);
                    //     const monthLabel = date.toLocaleString("default", {
                    //       month: "short",
                    //       year: "numeric",
                    //     });
                    //     const metCount = calculateMetCount(progress);

                    //     if (!monthMap[monthLabel]) monthMap[monthLabel] = [];
                    //     monthMap[monthLabel].push(metCount);
                    //   });

                    //   const categories = Object.keys(monthMap);

                    //   const data = categories.map((month) => {
                    //     const metCounts = monthMap[month];
                    //     let goodCount = 0;
                    //     let moderateCount = 0;
                    //     let poorCount = 0;

                    //     metCounts.forEach((metCount) => {
                    //       if (metCount === 3) goodCount++;
                    //       else if (metCount === 2) moderateCount++;
                    //       else poorCount++;
                    //     });

                    //     const total = goodCount + moderateCount + poorCount;
                    //     let goodPercentage = total
                    //       ? (goodCount / total) * 100
                    //       : 0;

                    //     if (goodPercentage === 0) {
                    //       goodPercentage = 5;
                    //     }

                    //     return Math.round(goodPercentage);
                    //   });

                    //   const colors = data.map((val) =>
                    //     val === 100
                    //       ? "#28a745"
                    //       : val >= 67
                    //       ? "#ffc107"
                    //       : "#dc3545"
                    //   );

                    //   const statuses = data.map((val) =>
                    //     val === 100 ? "Good" : val >= 67 ? "Moderate" : "Poor"
                    //   );

                    //   const options = {
                    //     chart: {
                    //       type: "bar",
                    //       toolbar: { show: false },
                    //     },
                    //     plotOptions: {
                    //       bar: {
                    //         distributed: true,
                    //         borderRadius: 6,
                    //       },
                    //     },
                    //     colors,
                    //     dataLabels: {
                    //       enabled: false,
                    //     },
                    //     xaxis: {
                    //       categories,
                    //       labels: { style: { fontSize: "13px" } },
                    //     },
                    //     yaxis: {
                    //       title: { text: "Good Progress Percentage (%)" },
                    //       min: 0,
                    //       max: 100,
                    //     },
                    //     tooltip: {
                    //       theme: "light",
                    //       y: {
                    //         formatter: (val, opts) =>
                    //           `${Math.round(val)}% - ${
                    //             statuses[opts.dataPointIndex]
                    //           }`,
                    //       },
                    //       x: {
                    //         formatter: (val) => `Month: ${val}`,
                    //       },
                    //     },
                    //   };

                    //   const series = [
                    //     {
                    //       name: "Monthly Evaluation",
                    //       data,
                    //     },
                    //   ];

                    //   return (
                    //     <div>
                    //       <Chart
                    //         options={options}
                    //         series={series}
                    //         type="bar"
                    //         height={400}
                    //       />
                    //     </div>
                    //   );
                    // })()

                    const monthMap = {};

                    const calculateMetCount = (progress) => {
                      const isMeetProgressPlanYes =
                        progress.meet_progress_plan === "YES";
                      const isSubmitThesisOnTime =
                        progress.submit_thesis &&
                        new Date(progress.submit_thesis) <=
                          new Date(progress.studyId.finish_date);
                      const isStudyCompletionOnTime =
                        progress.study_complition &&
                        new Date(progress.study_complition) <=
                          new Date(progress.studyId.finish_date);

                      const metCount = [
                        isMeetProgressPlanYes,
                        isSubmitThesisOnTime,
                        isStudyCompletionOnTime,
                      ].filter(Boolean).length;

                      return metCount;
                    };

                    progressList.forEach((progress) => {
                      const date = new Date(progress.progress_date);
                      const monthLabel = date.toLocaleString("default", {
                        month: "short",
                        year: "numeric",
                      });
                      const metCount = calculateMetCount(progress);

                      if (!monthMap[monthLabel]) monthMap[monthLabel] = [];
                      monthMap[monthLabel].push(metCount);
                    });

                    const categories = Object.keys(monthMap);

                    const data = categories.map((month) => {
                      const metCounts = monthMap[month];
                      let goodCount = 0;
                      let moderateCount = 0;
                      let poorCount = 0;

                      metCounts.forEach((metCount) => {
                        if (metCount === 3) goodCount++;
                        else if (metCount === 2) moderateCount++;
                        else poorCount++;
                      });

                      const total = goodCount + moderateCount + poorCount;
                      const goodPercentage = total
                        ? (goodCount / total) * 100
                        : 0;
                      const moderatePercentage = total
                        ? (moderateCount / total) * 100
                        : 0;
                      const poorPercentage = total
                        ? (poorCount / total) * 100
                        : 0;

                      // Return the highest percentage to determine the overall status
                      // This shows which category has the most records for that month
                      return {
                        good: Math.round(goodPercentage),
                        moderate: Math.round(moderatePercentage),
                        poor: Math.round(poorPercentage),
                        max: Math.max(
                          goodPercentage,
                          moderatePercentage,
                          poorPercentage
                        ),
                      };
                    });

                    const colors = data.map((val) => {
                      if (val.good === val.max && val.good > 0)
                        return "#28a745"; // Good is highest
                      if (val.moderate === val.max && val.moderate > 0)
                        return "#ffc107"; // Moderate is highest
                      return "#dc3545"; // Poor is highest
                    });

                    // Use the max value for the bar height
                    const chartData = data.map((val) => val.max);

                    const options = {
                      chart: {
                        type: "bar",
                        toolbar: { show: false },
                      },
                      plotOptions: {
                        bar: {
                          distributed: true,
                          borderRadius: 6,
                        },
                      },
                      colors,
                      dataLabels: {
                        enabled: false,
                      },
                      xaxis: {
                        categories,
                        labels: { style: { fontSize: "13px" } },
                      },
                      yaxis: {
                        title: { text: "Progress Percentage (%)" },
                        min: 0,
                        max: 100,
                      },
                      tooltip: {
                        theme: "light",
                        y: {
                          formatter: (val, opts) => {
                            const idx = opts.dataPointIndex;
                            const d = data[idx];
                            return `Good: ${d.good}% | Moderate: ${d.moderate}% | Poor: ${d.poor}%`;
                          },
                        },
                        x: {
                          formatter: (val) => `Month: ${val}`,
                        },
                      },
                    };

                    const series = [
                      {
                        name: "Monthly Evaluation",
                        data: chartData,
                      },
                    ];

                    return (
                      <div>
                        <Chart
                          options={options}
                          series={series}
                          type="bar"
                          height={400}
                        />
                      </div>
                    );
                  })()
                )}
              </div>
            </div>

            <div className="card zis-shadow mt-4 rounded-2">
              <div className="card-header mt-3 d-flex justify-content-between align-items-center">
                <h4 className="fw-semibold text-black mb-0">
                  Study progress records
                </h4>
              </div>

              <div className="card-body">
                <div className="table-responsive">
                  {error ? (
                    <div className="text-danger text-center">{error}</div>
                  ) : (
                    <DataTable
                      columns={columns}
                      data={progressList}
                      progressPending={loading}
                      progressComponent={
                        <div className="text-center py-3">
                          Loading progress records...
                        </div>
                      }
                      noDataComponent={
                        <div className="text-center py-3">
                          No progress records found
                        </div>
                      }
                      pagination
                      customStyles={HSTSCustomStyle}
                      highlightOnHover
                      responsive
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageProgressC;
